import React from 'react';
import PropTypes from 'prop-types';
import { Footer, Header } from '@stitch-fix/knit';
import { Box } from '@stitch-fix/mode-react';
import ErrorAlert from '../Gated/src/components/shared/ErrorAlert/ErrorAlert';
import ErrorBoundary from '../shared/ErrorBoundary/ErrorBoundary';
import { ProductDetailsPageProvider } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import ProductDetailsPageGraphQlConsumer from '../ProductDetailsPage/ProductDetailsPageGraphQlConsumer/ProductDetailsPageGraphQlConsumer';
import ProductDetailsPage from '../ProductDetailsPage';
import './styles.module.scss';
import KnitHeaderWrapper from '../KnitHeaderWrapper';
import BackToPreviousPageHeader from '../BackToPreviousPageHeader/BackToPreviousPageHeader';

/**
 * This is a "wrapper" for the ProductDetailsPage.
 */

const ProductDetailsPageWrapper = ({
  productSlug,
  externalProductId,
  canHideItem,
  canSaveItem,
  canScheduleAnchoredFix,
  hideItemSuccessPath,
  shopAllSizes,
  shoppingBagEmpty,
  gated,
  urlScope,
  newEntryPoints,
}: {
  productSlug: string;
  externalProductId: string;
  canHideItem?: boolean;
  canSaveItem?: boolean;
  canScheduleAnchoredFix?: boolean;
  hideItemSuccessPath?: string;
  shopAllSizes?: boolean;
  shoppingBagEmpty?: boolean;
  gated?: boolean;
  urlScope: string;
  newEntryPoints?: boolean;
}) => {
  const relativePath = window.location.pathname + window.location.search;
  const {
    client_region: region,
    error,
    headerActivePage,
    hideKnitFooter,
    hideKnitHeader,
  } = window.gon;

  const params = new URLSearchParams(window.location.search);
  const isStaticView = !!(
    params.get('inFixPreview') || params.get('styledFixShipment')
  );

  // When BackToPreviousPageHeader is rendered, we need to push the content down so BackToPreviousPageHeader
  // does not cover the page content. Why do we only do this if inFixPreview or styledFixShipment is true?
  // The reason is the KnitHeaderWrapper/Header renders an empty div that has the same height of the nav
  // to push down the page content.
  const paddingTop = isStaticView ? { sm: 3, lg: 3.5 } : { sm: 0, lg: 0 };

  return (
    <>
      {!gated && <Header region="US" loginRedirectUrl={relativePath} />}
      {gated && isStaticView && <BackToPreviousPageHeader />}
      {gated && !hideKnitHeader && !isStaticView && (
        <KnitHeaderWrapper path={headerActivePage} region={region} />
      )}
      {gated && error && error.heading && error.text && (
        <ErrorAlert heading={error.heading} text={error.text} />
      )}
      <ErrorBoundary>
        <ProductDetailsPageProvider
          initialProductSlug={productSlug}
          initialExternalProductId={externalProductId}
          gated={gated}
          urlScope={urlScope}
          hideItemSuccessPath={hideItemSuccessPath}
          shopAllSizes={shopAllSizes}
          canSaveItem={canSaveItem}
          canHideItem={canHideItem}
          canScheduleAnchoredFix={canScheduleAnchoredFix}
          shoppingBagEmpty={shoppingBagEmpty}
          newEntryPoints={newEntryPoints}
        >
          <ProductDetailsPageGraphQlConsumer>
            <Box pb={1} styleName="main-content-container" pt={paddingTop}>
              <main id="knit-main-content">
                <ProductDetailsPage />
              </main>
            </Box>
          </ProductDetailsPageGraphQlConsumer>
        </ProductDetailsPageProvider>
      </ErrorBoundary>
      {!gated && <Footer region="US" />}
      {gated && !hideKnitFooter && <Footer region={region} />}
      {/* Adding space below footer on mobile so that sticky button container
        doesn't cover footer when it reaches the bottom of the page */}
      <Box mb={{ sm: 4.5, md: 0 }} />
    </>
  );
};

ProductDetailsPageWrapper.propTypes = {
  productSlug: PropTypes.string,
  externalProductId: PropTypes.string,
  canHideItem: PropTypes.bool,
  canSaveItem: PropTypes.bool,
  canScheduleAnchoredFix: PropTypes.bool,
  hideItemSuccessPath: PropTypes.string,
  shopAllSizes: PropTypes.bool,
  shoppingBagEmpty: PropTypes.bool,
  gated: PropTypes.bool,
  newEntryPoints: PropTypes.bool,
};

ProductDetailsPageWrapper.defaults = {
  productSlug: undefined,
  externalProductId: undefined,
  canHideItem: false,
  canSaveItem: false,
  canScheduleAnchoredFix: false,
  hideItemSuccessPath: '/my-items/gallery',
  shopAllSizes: false,
  shoppingBagEmpty: false,
  gated: false,
  newEntryPoints: false,
};

export default ProductDetailsPageWrapper;
